import { KikErrorCodes, KikHttpStatusCodes as HttpStatusCode } from "@kikocosmeticsorg/uc-api-nest-common-fe";
import { setCoreSetting } from "@springtree/eva-sdk-core-settings";
import {
  EmptyResponseMessage,
  IEvaServiceDefinition,
  ListEntityTranslations,
  SvcListEntityTranslations,
  SvcListOrganizationUnitsDetailed,
  TranslationsListEntityTranslation,
} from "@springtree/eva-services-core";

import Eva from "~/services/eva";
import { ErrorResponse } from "~/shared/api/error-response.class";
import { EvaProductTypes } from "~/shared/eva/eva-product-types.enum";

import packageJson from "../../package.json";

export default class EvaAdmin extends Eva {
  static get instance(): EvaAdmin {
    if (!this._instance) {
      this._instance = new EvaAdmin();
    }

    return this._instance;
  }

  private static _instance: EvaAdmin;

  private _adminToken: string = process.env.EVA_ADMIN_TOKEN!;
  private _defaultLocale: string = "en-US";

  constructor({ locale, organizationUnitId }: { locale?: string; organizationUnitId?: string } = {}) {
    super({ locale, organizationUnitId: organizationUnitId || "1" });
    this._updateCoreSettings();
  }

  // PUBLIC
  getOrganizationUnitForLocale(locale: string) {
    return this.listOrganizationUnitDetailed().then((units) => {
      const country = locale.split("-")[1];
      const org = units.find((w: any) => w.CountryID!.toLowerCase() === country.toLowerCase())!;

      return {
        ...org,
        ID: org.ID!,
        BackendID: org.BackendID!,
      };
    });
  }

  listEntityTranslations(payload: ListEntityTranslations): Promise<TranslationsListEntityTranslation[] | undefined> {
    return this._getAdminService(SvcListEntityTranslations, payload).then((data) => {
      if (!data) {
        return void 0;
      }
      return data.Result?.Page;
    });
  }

  async listOrganizationUnitDetailed() {
    const response = await this._getAdminService(
      SvcListOrganizationUnitsDetailed,
      {
        PageConfig: {
          Filter: {
            TypeID: 2,
            OrganizationUnitSetID: process.env.NEXT_PUBLIC_EVA_ORGANIZATION_SET_BACKENDID!,
          },
        },
      },
      (req) => {
        req.headers.set("EVA-IDs-Mode", "ExternalIDs");
      }
    );

    return response!.Result?.Page || [];
  }

  updateLocale(locale: string): Promise<void> {
    return Eva.getOrganizationUnitId(locale).then((organizationUnitId) => {
      this.organizationUnitId = organizationUnitId;
      this.locale = locale;
      this._updateCoreSettings();
    });
  }

  async updateOrganizationUnit(organizationUnitId: string, locale?: string): Promise<void> {
    if (!locale) {
      const summary = await this.listOrganizationUnitSummaries().then((list) =>
        list.find((ou) => ou.ID === organizationUnitId)
      );
      locale = summary ? `${summary.LanguageID}-${summary.CountryID}` : this._defaultLocale;
    }
    this.organizationUnitId = organizationUnitId;
    this.locale = locale;
    this._updateCoreSettings();
  }

  // PROTECTED

  protected _getAdminService<T extends IEvaServiceDefinition>(
    serviceDefinition: new () => T,
    payload?: T["request"],
    preprocessFetch?: (req: Request) => void
  ) {
    return this.getService(serviceDefinition, payload, (fetchRequest) => {
      if (typeof preprocessFetch === "function") {
        preprocessFetch(fetchRequest);
      }
      fetchRequest.headers.set("Authorization", this._adminToken);
      // fetchRequest.headers.set("EVA-User-Agent", `${packageJson.name}@${packageJson.version}`);
    });
  }

  /**
   * Checks if the response contains an Error object and creates the Error response, since Eva SDK doesn't.
   */
  protected _handleEvaErrors<T extends EmptyResponseMessage>(r: T | void): void {
    if (r?.Error?.Code) {
      throw ErrorResponse.create({
        messages: r.Error.Message,
        errorCode: isNaN(+r.Error.Code) ? KikErrorCodes.GENERIC_EVA_ERROR : +r.Error.Code,
        status: HttpStatusCode.Conflict,
        data: r,
      });
    }
  }

  protected _updateCoreSettings(): void {
    setCoreSetting("appName", packageJson.name);
    setCoreSetting("appVersion", packageJson.version);
    setCoreSetting("language", this.locale || this._defaultLocale);
  }
}
